import React, { useState, useEffect, useRef } from 'react';
import { HeroImage } from '../common';
import { UnsplashCredit } from '../common/UnsplashCredit';
import { type SearchedDestination } from '../../services/atlasApi';
import { chooseExploreTarget } from '../../utils/exploreTarget';
import { useExploreSearch } from '../../hooks/useExploreSearch';
import './HeroSearch.css';

// Deliberately spanning the range Explore covers — an afternoon out, a season
// abroad, a month away — so the box does not read as a holiday-only search.
// Same three as mobile's, because it is the same question in two places.
const PLACEHOLDERS = [
  'An afternoon in Hudson…',
  'Cherry blossoms in Japan…',
  'Three weeks in Patagonia…',
];

interface HeroSearchProps {
  onPlanClick: (slug: string) => void;
  onDestinationClick: (slug: string) => void;
  onAskAI: (query: string) => void;
}

const HeroSearch: React.FC<HeroSearchProps> = ({
  onPlanClick, onDestinationClick, onAskAI,
}) => {
  const [query, setQuery] = useState('');
  const [phIdx, setPhIdx] = useState(0);
  // The query an Enter was pressed for, when it arrived before we knew that
  // query's destinations. It carries the query for the same reason `found`
  // does: as a bare boolean it meant "someone pressed Enter once", which then
  // fired against whatever was typed next — typing after a pending Enter
  // navigated with no second keypress, and shortening the box committed Ask AI
  // on its own. An intent belongs to the thing it was expressed about.
  const [pendingEnter, setPendingEnter] = useState<string | null>(null);
  // Synchronous latch. Clearing `query` is the thing that makes a second
  // selection impossible, but a setState is not visible until React re-renders
  // — so two Enter keydowns delivered in one batch both passed the guards and
  // navigated twice. A ref changes on the spot, which is what "already acted
  // on this" has to mean.
  const committed = useRef(false);

  // Rotate placeholders
  useEffect(() => {
    const timer = setInterval(() => setPhIdx(i => (i + 1) % PLACEHOLDERS.length), 3000);
    return () => clearInterval(timer);
  }, []);

  const isActive = query.length > 1;


  // The lookup — debounce, staleness rule, analytics, failure path — lives in
  // ONE hook now (slice 4c-3), because the map view grew its own box and two
  // copies of that effect is how the measurement drifts apart. The hook's
  // returns are stable per answer, so `commit`'s identity is too.
  const { places, plans: results } = useExploreSearch(query, isActive);

  const shownPlaces = places ?? [];
  const hasAnything = shownPlaces.length > 0 || results.length > 0;

  const commit = React.useCallback((destinations: SearchedDestination[]) => {
    if (committed.current) return;
    committed.current = true;
    setPendingEnter(null);
    setQuery('');
    // The ordering (place, then plan, then Ask AI) is shared with the trending
    // strip, which resolves the same kind of name — see `utils/exploreTarget`.
    const target = chooseExploreTarget(destinations, results, query);
    if (target.kind === 'destination') onDestinationClick(target.slug);
    else if (target.kind === 'plan') onPlanClick(target.slug);
    else onAskAI(target.query);
  }, [results, onDestinationClick, onPlanClick, onAskAI, query]);

  // Enter arrived before the lookup did. Act once it lands, rather than
  // falling through to Ask AI and throwing away the answer that was in flight.
  // Only for the query it was actually pressed for.
  useEffect(() => {
    if (pendingEnter !== null && pendingEnter === query && places !== null) {
      commit(places);
    }
  }, [pendingEnter, query, places, commit]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || !query) return;
    if (places === null) setPendingEnter(query);
    else commit(places);
  };

  // Typing again abandons a waiting Enter: whatever it was for, it wasn't this.
  // It also re-arms the latch — this is a new search, not the old one twice.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    committed.current = false;
    setPendingEnter(null);
    setQuery(e.target.value);
  };

  /** Picking a result by hand also cancels any Enter still waiting. */
  const choose = (act: () => void) => {
    if (committed.current) return;
    committed.current = true;
    setPendingEnter(null);
    setQuery('');
    act();
  };

  return (
    <div className="hero-search">
      {/* A gradient, not a photograph (r768 finding 3).

          This was one hard-coded Unsplash photograph, decorative, shown to
          every visitor of the discover page and credited nowhere — and
          uncreditable in principle: nothing in the codebase records who took
          it, so there is no name to print. A photograph we cannot credit must
          not be displayed, which leaves removing it. The gradient beneath was
          always there as the fallback.

          It is never given a `src`, so it is always the paper fallback
          (decision 25), and the scrim that darkened the old photograph is
          gone with it: everything above sits on paper (r1123 finding 1). */}
      <div className="hero-search__bg">
        <HeroImage fallbackKey="" size="hero" />
      </div>

      <div className="hero-search__input-wrap">
        <input
          type="text"
          className="hero-search__input"
          placeholder={PLACEHOLDERS[phIdx]}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      </div>

      {isActive && hasAnything && (
        <div className="hero-search__results">
          {shownPlaces.map(d => (
            /* The credit sits OUTSIDE the button, beside the photograph it
               credits — the pattern Explore's cards already ship on both
               platforms. It cannot go inside: an <a> nested in a <button> is
               invalid markup and the photographer link would not be reachable,
               which is the same as not crediting at all. */
            <div key={d.slug} className="hero-search__result-row">
              <button
                className="hero-search__result"
                onClick={() => choose(() => onDestinationClick(d.slug))}
              >
                <HeroImage src={d.thumb_url} fallbackKey={d.name} size="thumb-sm" />
                <div className="hero-search__result-text">
                  <span className="hero-search__result-title">{d.name}</span>
                  <span className="hero-search__result-meta">{d.where_sub || 'Destination'}</span>
                </div>
              </button>
              {d.thumb_url && d.thumb_attribution && (
                <UnsplashCredit
                  photographer={d.thumb_attribution.photographer}
                  profileLink={d.thumb_attribution.unsplash_link}
                  className="hero-search__credit"
                />
              )}
            </div>
          ))}
          {results.map(p => (
            <button
              key={p.slug}
              className="hero-search__result"
              onClick={() => choose(() => onPlanClick(p.slug))}
            >
              <HeroImage fallbackKey={p.title} size="thumb-sm" />
              <div className="hero-search__result-text">
                <span className="hero-search__result-title">{p.title}</span>
                {/* The hook, when the plan has one: it is why this plan and not
                    another, which a duration alone never says. */}
                <span className="hero-search__result-meta">
                  {p.hook || `${p.duration_days} days · ${p.destination}`}
                </span>
              </div>
            </button>
          ))}
          <button className="hero-search__ask-ai" onClick={() => choose(() => onAskAI(query.trim()))}>
            Ask AI about "{query}" →
          </button>
        </div>
      )}

      <div className="hero-search__bottom">
        <h2 className="hero-search__title">Plan your next trip</h2>
        <p className="hero-search__subtitle">Plans built by real people. Pick one and make it yours.</p>
        <button className="hero-search__cta" onClick={() => onAskAI('')}>Ask AI to build one</button>
      </div>
    </div>
  );
};

export default HeroSearch;
